/**
 * Cart Drawer Collection Slider - Fixed Version
 * Handles the product collection slider in the cart drawer
 */

class CartDrawerCollectionSlider {
  constructor() {
    this.slider = null;
    this.track = null;
    this.items = [];
    this.prevBtn = null;
    this.nextBtn = null;
    this.currentIndex = 0;
    this.itemsPerView = 2; // Default mobile
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationID = null;
    
    this.init();
  }

  init() {
    // Wait for DOM
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
    
    // Setup resize listener
    this.setupResizeListener();
  }

  setup() {
    this.slider = document.querySelector('.cart-drawer__collection-slider');
    
    if (!this.slider) {
      console.log('Collection slider not found');
      return;
    }

    this.track = this.slider.querySelector('.collection-slider__track');
    this.items = Array.from(this.slider.querySelectorAll('.collection-slider__item'));
    this.prevBtn = this.slider.querySelector('.collection-slider__nav--prev');
    this.nextBtn = this.slider.querySelector('.collection-slider__nav--next');

    if (!this.track || this.items.length === 0) {
      console.log('Slider track or items not found');
      return;
    }

    console.log(`Slider initialized with ${this.items.length} items`);

    this.itemsPerView = this.getItemsPerView();
    this.setupEventListeners();
    this.updateButtons();
    this.setupDragEvents();
  }

  getItemsPerView() {
    const width = window.innerWidth;
    if (width >= 768) {
      return 3; // Desktop: 3 items
    }
    return 2; // Mobile: 2 items
  }

  setupEventListeners() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.prev();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.next();
      });
    }
  }

  setupDragEvents() {
    // Mouse events
    this.track.addEventListener('mousedown', (e) => this.dragStart(e));
    this.track.addEventListener('mousemove', (e) => this.drag(e));
    this.track.addEventListener('mouseup', () => this.dragEnd());
    this.track.addEventListener('mouseleave', () => this.dragEnd());

    // Touch events
    this.track.addEventListener('touchstart', (e) => this.dragStart(e), { passive: true });
    this.track.addEventListener('touchmove', (e) => this.drag(e), { passive: true });
    this.track.addEventListener('touchend', () => this.dragEnd());

    // Prevent image drag
    this.track.querySelectorAll('img').forEach(img => {
      img.addEventListener('dragstart', (e) => e.preventDefault());
    });
    
    // Prevent link clicks during drag
    this.track.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', (e) => {
        if (this.isDragging) {
          e.preventDefault();
        }
      });
    });
  }

  dragStart(e) {
    this.isDragging = true;
    this.startPos = this.getPositionX(e);
    this.animationID = requestAnimationFrame(() => this.animation());
    this.track.style.cursor = 'grabbing';
    this.track.classList.remove('sliding');
  }

  drag(e) {
    if (!this.isDragging) return;
    
    const currentPosition = this.getPositionX(e);
    this.currentTranslate = this.prevTranslate + currentPosition - this.startPos;
  }

  dragEnd() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    cancelAnimationFrame(this.animationID);
    this.track.style.cursor = 'grab';
    this.track.classList.add('sliding');

    const movedBy = this.currentTranslate - this.prevTranslate;

    // Determine direction
    if (movedBy < -50 && this.currentIndex < this.maxIndex()) {
      this.currentIndex++;
    } else if (movedBy > 50 && this.currentIndex > 0) {
      this.currentIndex--;
    }

    this.setPositionByIndex();
  }

  getPositionX(e) {
    return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
  }

  animation() {
    this.setSliderPosition();
    if (this.isDragging) {
      requestAnimationFrame(() => this.animation());
    }
  }

  setSliderPosition() {
    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  next() {
    if (this.currentIndex >= this.maxIndex()) return;
    
    this.currentIndex++;
    this.setPositionByIndex();
  }

  prev() {
    if (this.currentIndex <= 0) return;
    
    this.currentIndex--;
    this.setPositionByIndex();
  }

  maxIndex() {
    return Math.max(0, this.items.length - this.itemsPerView);
  }

  setPositionByIndex() {
    this.track.classList.add('sliding');
    
    // Get actual item width including gap
    const firstItem = this.items[0];
    if (!firstItem) return;
    
    const itemWidth = firstItem.offsetWidth;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 12;
    const slideWidth = itemWidth + gap;
    
    // Calculate translate value
    this.currentTranslate = -(this.currentIndex * slideWidth);
    this.prevTranslate = this.currentTranslate;
    
    this.setSliderPosition();
    this.updateButtons();
  }

  updateButtons() {
    if (!this.prevBtn || !this.nextBtn) return;

    // Update prev button
    this.prevBtn.disabled = this.currentIndex <= 0;

    // Update next button
    this.nextBtn.disabled = this.currentIndex >= this.maxIndex();
  }

  setupResizeListener() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const newItemsPerView = this.getItemsPerView();
        
        if (newItemsPerView !== this.itemsPerView) {
          this.itemsPerView = newItemsPerView;
          
          // Adjust current index if needed
          if (this.currentIndex > this.maxIndex()) {
            this.currentIndex = this.maxIndex();
          }
          
          this.setPositionByIndex();
        }
      }, 250);
    });
  }

  // Method to reinitialize when cart updates
  reinit() {
    this.currentIndex = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    
    setTimeout(() => {
      this.setup();
    }, 100);
  }
}

// Initialize the slider
let cartSliderInstance = null;

function initCartDrawerSlider() {
  // Destroy previous instance if exists
  if (cartSliderInstance) {
    cartSliderInstance = null;
  }
  
  // Create new instance
  cartSliderInstance = new CartDrawerCollectionSlider();
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartDrawerSlider);
} else {
  initCartDrawerSlider();
}

// Reinitialize when cart updates
document.addEventListener('cart:refresh', () => {
  console.log('Cart refreshed, reinitializing slider');
  setTimeout(() => {
    initCartDrawerSlider();
  }, 200);
});

// Listen for cart drawer opening via mutation observer
const observeCartDrawer = () => {
  const cartDrawer = document.querySelector('cart-drawer');
  
  if (!cartDrawer) {
    console.log('Cart drawer element not found');
    return;
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.attributeName === 'class' || mutation.attributeName === 'open') {
        const isOpen = cartDrawer.hasAttribute('open') || !cartDrawer.classList.contains('is-empty');
        
        if (isOpen) {
          console.log('Cart drawer opened, initializing slider');
          setTimeout(() => {
            initCartDrawerSlider();
          }, 300);
        }
      }
    });
  });

  observer.observe(cartDrawer, { 
    attributes: true,
    attributeFilter: ['class', 'open']
  });
};

// Start observing
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeCartDrawer);
} else {
  observeCartDrawer();
}

// Also listen for Shopify cart events
document.addEventListener('shopify:section:load', () => {
  console.log('Shopify section loaded');
  setTimeout(initCartDrawerSlider, 200);
});